import Vue from 'vue'
import CONFIG from '../config/GIS_Config'
import { updateTopology } from '../config/GIS_Config'
import { axios } from '@/utils/request'

/**
 * 生成管径渲染样式
 * @param {*} configData 样式列数据
 * @param {*} styleName 样式名称
 */
export function generateDynamicSLD(configData, styleName) {
  // 验证配置数据
  if (!configData || configData.length === 0) {
    return this.defaultSLD
  }

  // 生成Rule部分
  const rules = configData
    .map(config => {
      const colour = config.colour || '#000000'
      const colour2 = config.colour2 || colour
      const width = config.lineWidth || 1
      const innerWidth = width
      const outerWidth = width + 1

      // 生成描边Symbolizer：colour2为空时退化为「外框+单色实线」，否则「外框+双色虚线交替」
      let strokeXml = `
        <LineSymbolizer>
          <Stroke>
            <CssParameter name="stroke">#333333</CssParameter>
            <CssParameter name="stroke-width">${outerWidth}</CssParameter>
          </Stroke>
        </LineSymbolizer>
        <LineSymbolizer>
          <Stroke>
            <CssParameter name="stroke">${colour2}</CssParameter>
            <CssParameter name="stroke-width">${innerWidth}</CssParameter>
          </Stroke>
        </LineSymbolizer>
        <LineSymbolizer>
          <Stroke>
            <CssParameter name="stroke">${colour}</CssParameter>
            <CssParameter name="stroke-width">${innerWidth}</CssParameter>
            <CssParameter name="stroke-dasharray">10 3</CssParameter>
            <CssParameter name="stroke-dashoffset">10</CssParameter>
          </Stroke>
        </LineSymbolizer>`

      // 处理边界条件（左开右闭）
      let filterXml = ''

      // 特殊情况：全范围规则（min和max都为null）
      if (config.minLimit === null && config.maxLimit === null) {
        return `
      <Rule>${strokeXml}
      </Rule>`
      }

      // 普通规则
      if (config.minLimit === null) {
        // 上限条件 (x ≤ max)
        filterXml = `
        <ogc:PropertyIsLessThanOrEqualTo>
          <ogc:PropertyName>diameter</ogc:PropertyName>
          <ogc:Literal>${config.maxLimit}</ogc:Literal>
        </ogc:PropertyIsLessThanOrEqualTo>`
      } else if (config.maxLimit === null) {
        // 下限条件 (x > min)
        filterXml = `
        <ogc:PropertyIsGreaterThan>
          <ogc:PropertyName>diameter</ogc:PropertyName>
          <ogc:Literal>${config.minLimit}</ogc:Literal>
        </ogc:PropertyIsGreaterThan>`
      } else {
        // 区间条件 (min < x ≤ max)
        filterXml = `
        <ogc:And>
          <ogc:PropertyIsGreaterThan>
            <ogc:PropertyName>diameter</ogc:PropertyName>
            <ogc:Literal>${config.minLimit}</ogc:Literal>
          </ogc:PropertyIsGreaterThan>
          <ogc:PropertyIsLessThanOrEqualTo>
            <ogc:PropertyName>diameter</ogc:PropertyName>
            <ogc:Literal>${config.maxLimit}</ogc:Literal>
          </ogc:PropertyIsLessThanOrEqualTo>
        </ogc:And>`
      }

      return `
      <Rule>
        <ogc:Filter>
          ${filterXml}
        </ogc:Filter>${strokeXml}
      </Rule>`
    })
    .join('\n')

  // 生成完整的SLD
  return `<?xml version="1.0" encoding="UTF-8"?>
<StyledLayerDescriptor version="1.0.0" 
    xmlns="http://www.opengis.net/sld" 
    xmlns:ogc="http://www.opengis.net/ogc"
    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd">
  <NamedLayer>
    <Name>tp_pipeline</Name>
    <UserStyle>
      <Title>${styleName}</Title>
      <FeatureTypeStyle>
        ${rules}
      </FeatureTypeStyle>
    </UserStyle>
  </NamedLayer>
</StyledLayerDescriptor>`
}

/**
 * 生成点图标SLD
 * @param {Array} configData 配置数据
 * @param {string} styleName 样式名称
 */
export function generateIconSLD(configData, styleName) {
  // 节点类型到颜色和形状的映射（作为后备方案）
  const nodeTypeToStyle = {
    'NODE_PIPELINE_TYPE_VALVE': { color: '#FF0000', shape: 'circle' },      // 红色圆形 - 阀门
    'NODE_PIPELINE_TYPE_HYDRANT': { color: '#FF4500', shape: 'square' },    // 橙红色方形 - 消防栓
    'NODE_PIPELINE_TYPE_TOWER': { color: '#0000FF', shape: 'triangle' },   // 蓝色三角形 - 水塔
    'NODE_PIPELINE_TYPE_BOOSTER_PUMP_STATION': { color: '#800080', shape: 'star' }, // 紫色星形 - 泵站
    'NODE_PIPELINE_TYPE_METER': { color: '#008000', shape: 'circle' },     // 绿色圆形 - 水表
    'NODE_PIPELINE_TYPE_COMMON': { color: '#808080', shape: 'circle' },     // 灰色圆形 - 普通节点
    'NODE_PIPELINE_TYPE_PM': { color: '#FF69B4', shape: 'circle' },        // 粉红色圆形 - 压力表
    'NODE_PIPELINE_TYPE_FM': { color: '#4169E1', shape: 'square' },         // 蓝色方形 - 流量计
    'NODE_PIPELINE_TYPE_WM': { color: '#00CED1', shape: 'triangle' },      // 青色三角形 - 液位计
    'NODE_PIPELINE_TYPE_WQ': { color: '#32CD32', shape: 'diamond' },       // 绿色菱形 - 水质
    'NODE_PIPELINE_TYPE_EV': { color: '#FFD700', shape: 'star' },           // 金色星形 - 电控阀
    // 兼容数字类型
    '1': { color: '#FF0000', shape: 'circle' },
    '2': { color: '#FF4500', shape: 'square' },
    '3': { color: '#0000FF', shape: 'triangle' },
    '4': { color: '#800080', shape: 'star' },
    '5': { color: '#008000', shape: 'circle' },
    '6': { color: '#808080', shape: 'circle' },
    '7': { color: '#FF69B4', shape: 'circle' },
    '8': { color: '#4169E1', shape: 'square' },
    '9': { color: '#00CED1', shape: 'triangle' },
    '10': { color: '#32CD32', shape: 'diamond' },
    '11': { color: '#FFD700', shape: 'star' },
  }

  const rulesXml = configData
    .map(config => {
      // 如果没有nodeType，跳过此配置
      if (!config.nodeType) {
        console.warn('跳过没有nodeType的配置项:', config)
        return ''
      }

      // 检查是否有上传的图标
      const hasIcon = config.icon && config.icon.trim() !== ''

      console.log(`节点类型 ${config.nodeType} -> ${hasIcon ? '图标: ' + config.icon : '默认几何图形'}`)

      if (hasIcon) {
        // 图标已上传到 GeoServer styles 目录，用 GeoServer 自身 URL 引用
        // GeoServer 渲染时通过本地 HTTP 获取，不存在跨域和网络问题
        const iconUrl = `${CONFIG.GIS_URL}/geoserver/styles/${config.icon}`
        // 固定背景（SVG），先于PNG绘制，作为图标底图
        const bgIconUrl = `${CONFIG.GIS_URL}/geoserver/styles/base/node_bg.svg`
        const bgSize = 32
        const iconSize = 25
        // 文字标注（name）参数：放在图标下方
        // 字体需GeoServer服务器已安装，显示方框则改为服务器实际可用字体(如 Microsoft YaHei / Noto Sans CJK SC)
        const labelFont = 'SimSun'
        const labelFontSize = 12
        const labelColor = '#333333'
        const labelHaloColor = '#FFFFFF'
        const labelDisplacementX = 0       // 水平居中（相对锚点）
        const labelDisplacementY = -18     // 向下（SLD Y 正向上，负值向下），值≈图标半径，使文字落在图标下方

        return `
          <Rule>
            <ogc:Filter>
              <ogc:PropertyIsEqualTo>
                <ogc:PropertyName>type</ogc:PropertyName>
                <ogc:Literal>${config.nodeType}</ogc:Literal>
              </ogc:PropertyIsEqualTo>
            </ogc:Filter>
            <PointSymbolizer>
              <Graphic>
                <ExternalGraphic>
                  <OnlineResource xmlns:xlink="http://www.w3.org/1999/xlink" xlink:type="simple" xlink:href="${bgIconUrl}"/>
                  <Format>image/svg+xml</Format>
                </ExternalGraphic>
                <Size>${bgSize}</Size>
              </Graphic>
            </PointSymbolizer>
            <PointSymbolizer>
              <Graphic>
                <ExternalGraphic>
                  <OnlineResource xmlns:xlink="http://www.w3.org/1999/xlink" xlink:type="simple" xlink:href="${iconUrl}"/>
                  <Format>image/png</Format>
                </ExternalGraphic>
                <Size>${iconSize}</Size>
              </Graphic>
            </PointSymbolizer>
            <TextSymbolizer>
              <Label>
                <ogc:PropertyName>name</ogc:PropertyName>
              </Label>
              <Font>
                <CssParameter name="font-family">${labelFont}</CssParameter>
                <CssParameter name="font-size">${labelFontSize}</CssParameter>
                <CssParameter name="font-weight">bold</CssParameter>
              </Font>
              <LabelPlacement>
                <PointPlacement>
                  <AnchorPoint>
                    <AnchorPointX>0.5</AnchorPointX>
                    <AnchorPointY>1.0</AnchorPointY>
                  </AnchorPoint>
                  <Displacement>
                    <DisplacementX>${labelDisplacementX}</DisplacementX>
                    <DisplacementY>${labelDisplacementY}</DisplacementY>
                  </Displacement>
                  <Rotation>0</Rotation>
                </PointPlacement>
              </LabelPlacement>
              <Halo>
                <Radius>1</Radius>
                <Fill>
                  <CssParameter name="fill">${labelHaloColor}</CssParameter>
                </Fill>
              </Halo>
              <Fill>
                <CssParameter name="fill">${labelColor}</CssParameter>
              </Fill>
              <VendorOption name="autoWrap">80</VendorOption>
              <VendorOption name="maxDisplacement">20</VendorOption>
              <VendorOption name="partials">true</VendorOption>
              <VendorOption name="goodies">true</VendorOption>
            </TextSymbolizer>
          </Rule>
        `
      } else {
        // 使用默认几何图形作为后备
        const style = nodeTypeToStyle[config.nodeType] || { color: '#808080', shape: 'circle' }

        // 根据形状生成对应的WellKnownName
        const wellKnownName = {
          'circle': 'circle',
          'square': 'square',
          'triangle': 'triangle',
          'star': 'star',
          'diamond': 'diamond'
        }[style.shape] || 'circle'

        return `
          <Rule>
            <ogc:Filter>
              <ogc:PropertyIsEqualTo>
                <ogc:PropertyName>type</ogc:PropertyName>
                <ogc:Literal>${config.nodeType}</ogc:Literal>
              </ogc:PropertyIsEqualTo>
            </ogc:Filter>
            <PointSymbolizer>
              <Graphic>
                <Mark>
                  <WellKnownName>${wellKnownName}</WellKnownName>
                  <Fill>
                    <CssParameter name="fill">${style.color}</CssParameter>
                  </Fill>
                  <Stroke>
                    <CssParameter name="stroke">#000000</CssParameter>
                    <CssParameter name="stroke-width">1</CssParameter>
                  </Stroke>
                </Mark>
                <Size>20</Size>
              </Graphic>
            </PointSymbolizer>
          </Rule>
        `
      }
    })
    .filter(rule => rule.trim() !== '')  // 过滤掉空的规则
    .join('\n')

  return `<?xml version="1.0" encoding="UTF-8"?>
<StyledLayerDescriptor version="1.0.0"
                       xmlns="http://www.opengis.net/sld"
                       xmlns:ogc="http://www.opengis.net/ogc"
                       xmlns:xlink="http://www.w3.org/1999/xlink"
                       xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
                       xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd">
  <NamedLayer>
    <Name>point_style</Name>
    <UserStyle>
      <Title>${styleName}</Title>
      <FeatureTypeStyle>
        ${rulesXml}
      </FeatureTypeStyle>
    </UserStyle>
  </NamedLayer>
</StyledLayerDescriptor>`
}

/**
 * 检查样式是否存在
 * @param {string} styleName 样式名称
 * @returns {Promise<boolean>} 样式是否存在
 */
async function checkStyleExists(styleName) {
  try {
    const url = CONFIG.SERVICE_PATHS.SLD_URL(styleName)
    const response = await axios({
      url,
      method: 'HEAD',
      headers: {
        Authorization: 'Basic ' + btoa(`${CONFIG.GEO_SERVER_AUTH.username}:${CONFIG.GEO_SERVER_AUTH.password}`),
      },
    })
    console.log('检查样式存在')
    return true
  } catch (error) {
    if (error.response && error.response.status === 404) {
      console.log('检查样式不存在')
      return false
    }
  }
}

/**
 * 创建新样式
 * @param {string} styleName 样式名称
 * @param {string} sldContent SLD内容
 * @returns {Promise} axios响应
 */
async function createStyle(styleName, sldContent) {
  // 创建样式的URL与更新不同
  const createUrl = CONFIG.SERVICE_PATHS.SLD_URL(styleName).replace(`styles/${styleName}`, 'styles')

  return axios({
    url: createUrl,
    method: 'POST',
    params: {
      name: styleName,
    },
    headers: {
      'Content-Type': 'application/vnd.ogc.sld+xml',
      Authorization: 'Basic ' + btoa(`${CONFIG.GEO_SERVER_AUTH.username}:${CONFIG.GEO_SERVER_AUTH.password}`),
    },
    data: sldContent,
  })
}
/**
 * 更新或创建GeoServer样式
 */
export async function updateOrCreateGeoServerStyle(customizeConfigItem) {
  console.log('更新或创建GeoServer样式--接收参数', customizeConfigItem)
  const styleName = customizeConfigItem.customizeConfigScheme.customizeType
  try {
    let sldContent = ''
    // 判断当前样式类型
    if (customizeConfigItem.customizeConfigScheme.customizeType == CONFIG.SLDStyle.CUSTOMIZE_CONFIG_TYPE_NODE_ICON) {
      console.log('更新或创建GeoServer样式--图标配置')
      sldContent = generateIconSLD(customizeConfigItem.customizeConfigDataList, styleName)
    } else if (
      customizeConfigItem.customizeConfigScheme.customizeType == CONFIG.SLDStyle.CUSTOMIZE_CONFIG_TYPE_LINE_COMMON
    ) {
      console.log('更新或创建GeoServer样式--管径渲染')
      // 2. 生成SLD
      sldContent = generateDynamicSLD(customizeConfigItem.customizeConfigDataList, styleName)
    } else if (
      customizeConfigItem.customizeConfigScheme.customizeType == CONFIG.SLDStyle.CUSTOMIZE_CONFIG_TYPE_LINE_PRESSURE
    ) {
      console.log('更新或创建GeoServer样式--压力渲染')
    } else if (
      customizeConfigItem.customizeConfigScheme.customizeType == CONFIG.SLDStyle.CUSTOMIZE_CONFIG_TYPE_ZOOM_LEVEL
    ) {
      console.log('更新或创建GeoServer样式--分级渲染:无需更新SLD文件')
      return
    }

    // 3. 检查样式是否存在
    const exists = await checkStyleExists(styleName)
    // 4. 根据是否存在进行创建或更新
    let response
    if (exists) {
      // 存在 - 更新
      response = await axios({
        url: CONFIG.SERVICE_PATHS.SLD_URL(styleName),
        method: 'PUT',
        headers: {
          'Content-Type': 'application/vnd.ogc.sld+xml',
          Authorization: 'Basic ' + btoa(`${CONFIG.GEO_SERVER_AUTH.username}:${CONFIG.GEO_SERVER_AUTH.password}`),
        },
        data: sldContent,
      })
      console.log('样式更新成功')
    } else {
      // 不存在 - 创建
      response = await createStyle(styleName, sldContent)
      console.log('样式创建成功')
    }

    return response
  } catch (error) {
    console.error('SLD操作失败:', error)

    // 处理特定错误情况
    if (error.response) {
      const { status, data } = error.response
      console.error(`GeoServer错误 ${status}:`, data)

      // 尝试解析GeoServer错误详情
      try {
        const parser = new DOMParser()
        const xmlDoc = parser.parseFromString(data, 'text/xml')
        const exceptionText = xmlDoc.getElementsByTagName('ExceptionText')[0]?.textContent
        if (exceptionText) {
          console.error('GeoServer错误详情:', exceptionText)
        }
      } catch (e) {
        // 无法解析错误详情
      }
    }

    throw error
  }
}

export async function updateGeoServerStyle(mainType) {
  updateTopology()
  CONFIG.Topology.customizeConfigList.forEach(element => {
    if (element.customizeConfigScheme.customizeType == mainType) {
      console.log('更新样式类型', mainType)
      updateOrCreateGeoServerStyle(element)
    }
  })
}

/**
 * 将图标文件上传到 GeoServer 本地 styles 目录
 * 这样 SLD 中可以用 file:styles/xxx.png 引用，不依赖网络
 * @param {Array} iconList - 图标文件名列表，如 ['fm_xxx.png', 'ylb_xxx.png']
 */
export async function uploadIconsToGeoServer(iconList) {
  const geoServerRestUrl = CONFIG.GIS_URL + '/geoserver/rest'
  const auth = 'Basic ' + btoa(`${CONFIG.GEO_SERVER_AUTH.username}:${CONFIG.GEO_SERVER_AUTH.password}`)

  let success = 0
  let failed = 0

  for (const icon of iconList) {
    try {
      // 通过代理下载图标（开发环境走 webpack devServer 代理，生产环境走 nginx 代理）
      // 使用相对路径 /jeecg-gis/... 避免跨域 CORS 问题
      const iconUrl = `/jeecg-gis/customizeConfigScheme/${icon}`
      console.log(`[uploadIcons] 下载图标: ${iconUrl}`)
      const response = await fetch(iconUrl)
      if (!response.ok) {
        console.warn(`[uploadIcons] 下载失败 ${icon}: ${response.status}`)
        failed++
        continue
      }
      const blob = await response.blob()

      // 上传到 GeoServer styles 目录
      const uploadUrl = `${geoServerRestUrl}/resource/styles/${icon}`
      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Authorization': auth,
          'Content-Type': 'image/png',
        },
        body: blob,
      })
      if (uploadRes.ok || uploadRes.status === 201) {
        console.log(`[uploadIcons] 上传成功: ${icon}`)
        success++
      } else {
        console.warn(`[uploadIcons] 上传失败 ${icon}: ${uploadRes.status}`)
        failed++
      }
    } catch (e) {
      console.warn(`[uploadIcons] 处理图标失败 ${icon}:`, e)
      failed++
    }
  }

  console.log(`[uploadIcons] 完成: 成功 ${success}, 失败 ${failed}`)
  return { success, failed }
}
